import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { RuntimeAnswer } from '../../types/runtime';

interface Props {
  answer: RuntimeAnswer;
  onEvidenceSelect?: (evidenceId: string) => void;
}

function stringField(item: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

function recordArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    : [];
}

function EvidenceMarkdown({ content }: { content: string }) {
  return (
    <div className="space-y-3 leading-relaxed text-emerald-950">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-base font-semibold leading-snug text-emerald-950">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-4 border-t border-emerald-200 pt-3 text-sm font-semibold leading-snug text-emerald-950">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-3 text-sm font-semibold leading-snug text-emerald-900">
              {children}
            </h3>
          ),
          p: ({ children }) => <p className="leading-relaxed">{children}</p>,
          ul: ({ children }) => (
            <ul className="ml-5 list-disc space-y-1 leading-relaxed">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="ml-5 list-decimal space-y-1 leading-relaxed">{children}</ol>
          ),
          li: ({ children }) => <li className="pl-1">{children}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-emerald-950">{children}</strong>
          ),
          em: ({ children }) => <em className="text-emerald-900">{children}</em>,
          hr: () => <hr className="border-emerald-200" />,
          code: ({ children }) => (
            <code className="rounded bg-emerald-100 px-1 py-0.5 text-[0.85em] text-emerald-950">
              {children}
            </code>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              className="font-medium text-emerald-700 underline underline-offset-2"
              target="_blank"
              rel="noreferrer"
            >
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export function DocumentEvidenceCard({ answer, onEvidenceSelect }: Props) {
  const sources = recordArray(answer.source_references?.length ? answer.source_references : answer.sources);
  const spans = recordArray(
    answer.evidence_references?.length ? answer.evidence_references : answer.evidence_spans,
  );
  const citations = recordArray(answer.citations);
  const sourcePolicy = answer.source_policy ?? {};
  const renderingContract =
    typeof sourcePolicy.rendering_contract === 'object' && sourcePolicy.rendering_contract !== null
      ? (sourcePolicy.rendering_contract as Record<string, unknown>)
      : {};
  const isEphemeral = answer.kind === 'document_ephemeral_summary';
  const isDeepCorpus = answer.kind === 'deep_corpus_synthesis';
  const shouldRenderMarkdown = !isEphemeral;
  const badge = isEphemeral ? 'Word text' : isDeepCorpus ? 'Deep corpus evidence' : 'Document evidence';
  const evidenceSpanCount =
    typeof answer.evidence_span_count === 'number'
      ? answer.evidence_span_count
      : typeof answer.evidence_count === 'number'
        ? answer.evidence_count
        : null;
  const sourcePolicyLabel = isEphemeral
    ? 'Transient Word text'
    : isDeepCorpus
      ? 'EdgeCite validated corpus'
      : 'DMS verified evidence';
  const wordHighlighting = stringField(renderingContract, 'word_highlighting');

  return (
    <article className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded bg-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-900">
          {badge}
        </span>
        {typeof evidenceSpanCount === 'number' && (
          <span className="text-xs text-emerald-800">
            {evidenceSpanCount} evidence span{evidenceSpanCount === 1 ? '' : 's'}
          </span>
        )}
        {isDeepCorpus && typeof answer.source_count === 'number' && answer.source_count > 0 && (
          <span className="text-xs text-emerald-800">
            {answer.source_count} source{answer.source_count === 1 ? '' : 's'}
          </span>
        )}
        {typeof answer.citation_count === 'number' && answer.citation_count > 0 && (
          <span className="text-xs text-emerald-800">
            {answer.citation_count} citation{answer.citation_count === 1 ? '' : 's'}
          </span>
        )}
        <span className="text-xs text-emerald-800">{sourcePolicyLabel}</span>
      </div>

      {shouldRenderMarkdown ? (
        <EvidenceMarkdown content={answer.content} />
      ) : (
        <p className="whitespace-pre-line leading-relaxed">{answer.content}</p>
      )}

      {sources.length > 0 && (
        <div className="mt-3 border-t border-emerald-200 pt-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
            Sources
          </div>
          <ul className="space-y-1">
            {sources.slice(0, 3).map((source, index) => {
              const title =
                stringField(source, 'label', 'title', 'documentId', 'document_id') ??
                `Source ${index + 1}`;
              const sourceRef = stringField(source, 'source_ref');
              const version = stringField(source, 'versionId', 'version_id');
              return (
                <li key={`${title}-${index}`} className="text-xs text-emerald-900">
                  {sourceRef ? (
                    <span className="mr-1 rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800">
                      {sourceRef}
                    </span>
                  ) : null}
                  {title}
                  {version ? <span className="text-emerald-700"> · version {version}</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {spans.length > 0 && (
        <div className="mt-3 space-y-2">
          {spans.slice(0, 2).map((span, index) => {
            const quote = stringField(span, 'quote', 'highlight_text');
            if (!quote) return null;
            const title = stringField(span, 'source_label', 'title');
            const evidenceRef = stringField(span, 'label', 'evidence_ref');
            const sourceRef = stringField(span, 'source_ref');
            const evidenceId =
              stringField(span, 'id', 'evidence_id', 'chunk_id') ??
              evidenceRef ??
              `E${index + 1}`;
            return (
              <button
                key={`${quote.slice(0, 24)}-${index}`}
                type="button"
                onClick={() => onEvidenceSelect?.(evidenceId)}
                className="block w-full border-l-2 border-emerald-300 pl-3 text-left text-xs leading-relaxed text-emerald-900 hover:border-emerald-500 hover:bg-emerald-100/40"
              >
                <div className="mb-1 flex flex-wrap items-center gap-1.5 font-medium">
                  {evidenceRef ? (
                    <span className="rounded bg-emerald-200 px-1.5 py-0.5 text-[10px] text-emerald-900">
                      {evidenceRef}
                    </span>
                  ) : null}
                  {title ? <span>{title}</span> : null}
                  {sourceRef ? <span className="text-emerald-700">from {sourceRef}</span> : null}
                </div>
                <p>{quote}</p>
              </button>
            );
          })}
        </div>
      )}

      {citations.length > 0 && (
        <div className="mt-3 text-xs text-emerald-800">
          Citations validated by EdgeCite runtime policy.
        </div>
      )}

      {wordHighlighting ? (
        <div className="mt-3 text-[11px] text-emerald-700">
          Highlight contract: {wordHighlighting}
        </div>
      ) : null}
    </article>
  );
}
